import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import marked from 'marked'
import truncate from 'truncate'
import * as actions from '../../actions'
import getUrlParameter from '../../helpers/getUrlParameter'
import { doTrack } from '../../helpers/TaggingSerializer'

import Helmet from 'react-helmet'
import Sidebar from '../../components/Sidebar/Sidebar'
import LoadingError from '../../components/LoadingError/LoadingError'
import LiveChat from '../../components/LiveChat/LiveChat'
import FAQLists from '../../components/FAQLists/FAQLists'
import SearchBar from '../../components/SearchBar/SearchBar'
import NotFound from './NotFound'

import './FAQ.scss'

class HelpFAQ extends PureComponent {
  static waitForDataServer (dispatch, params, req, res) {
    return Promise
      .all([
        dispatch(actions.fetchSidebarNav(true)),
        dispatch(actions.fetchFAQContent(params.faqId, true))
      ])
      .then(([sidebar, faq]) => {
        if (!faq.payload.active) {
          res.statusCode = 404
        }
      })
  }

  static waitForDataClient (store) {
    return (nextState, transition, callback) => {
      const { dispatch, getState } = store
      const faq = getState().faq

      if (faq.faq && faq.faq.faqId === nextState.params.faqId) {
        return callback()
      }

      dispatch(actions.fetchFAQContent(nextState.params.faqId))
        .then(() => { callback() })
        .catch(() => { callback() })
    }
  }

  constructor () {
    super()

    this.state = {
      searchTerm: ''
    }
  }

  componentWillReceiveProps (newProps) {
    if (this.props.routeParams.faqId !== newProps.routeParams.faqId) {
      this.fireAnalytics()
    }
  }

  componentDidMount () {
    this.fetchData(this.props.routeParams.faqId)
    if (getUrlParameter('search')) {
      this.setState({
        searchTerm: getUrlParameter('search')
      })
    }
  }

  fireAnalytics = () => {
    if (window.digitalData) {
      window.digitalData.page.pageInfo.pageName = 'ar:help:faq:details:' + this.props.faq.id
      window.digitalData.page.pageInfo.templateTitle = 'FAQ_details'
    }

    doTrack('help_initialLoad')
  }

  fetchData = (faqId) => {
    const { actions, sidebar } = this.props

    if (!sidebar.length) actions.fetchSidebarNav()
    this.fireAnalytics()
  }

  render () {
    const { error, sidebar, faq, loading } = this.props

    if (error) return <LoadingError />

    const ccn = 'faq'
    const pageCcn = 'page'

    const metaData = {
      title: 'Help & FAQs - ' + faq.question,
      meta: [{
        name: 'description',
        content: truncate(faq.answer, 50)
      }, {
        name: 'keywords',
        content: faq.keywords
      }, {
        name: 'robots',
        content: 'index, follow'
      }],
      link: [{
        rel: 'canonical',
        href: 'https://www.argos.co.uk/help/faq/' + faq.id
      }]
    }
    if (faq.question === null || faq.question === '' || faq.answer === 'This entry is in revision and can not be displayed.') return <NotFound />
    const isValidFaq = (faq.answer !== 'Access denied.')

    return (
      <div className={ccn + ' ' + pageCcn + ' container'}>
        <div id='helpfaqcontent' tabIndex='-1' />
        <Helmet {...metaData} />

        <div className='xs-row xs-row--negative'>

          <Sidebar links={sidebar} loading={loading} />

          <div className='xs-12 lg-9 content'>

            <SearchBar position='top' />

            <h2 className='page__title'>{faq.question}</h2>

            <div className='page__content' dangerouslySetInnerHTML={{ __html: marked(faq.answer) }} />

            <div className={ccn + '__nav'}>
              {
                isValidFaq && faq.previous !== 0 &&
                <a className={ccn + '__button'} href={'/help/faq/' + faq.previous}>
                  <span className='argos-icon icon-UIE-Controls-Chevron-Left' aria-hidden='true' />
                  previous
                </a>
              }
              <span />
              {
                isValidFaq && faq.next !== 0 &&
                <a className={ccn + '__button'} href={'/help/faq/' + faq.next}>
                  next
                  <span className='argos-icon icon-UIE-Controls-Chevron-Right' aria-hidden='true' />
                </a>
              }
            </div>

            {
              isValidFaq && faq.related_faqs.length &&
              <FAQLists
                inverse
                title={'Are these FAQs more helpful?'}
                faqs={faq.related_faqs} />
            }

            <span id='livechat-anchor' className='livechat-anchor' />

            {
              this.state.searchTerm !== '' &&
              <a className='back-to-link back-to-link--search-results' href={'/help/search/' + this.state.searchTerm}>Back to search results</a>
            }

            <a className='back-to-link back-to-link--help-home' href='/help/'>Back to help home</a>

            <LiveChat
              actions={this.props.actions}
              chatHasEnded={this.props.chatHasEnded}
              chatLoading={this.props.chatLoading}
              chatOnline={this.props.chatOnline}
            />

            <hr />

            <SearchBar position='bottom' />

          </div>
        </div>
      </div>
    )
  }
}

HelpFAQ.propTypes = {
  error: PropTypes.bool,
  sidebar: PropTypes.array,
  faq: PropTypes.object,
  loading: PropTypes.bool,
  actions: PropTypes.object,
  routeParams: PropTypes.object,
  chatHasEnded: PropTypes.bool,
  chatLoading: PropTypes.bool,
  chatOnline: PropTypes.bool
}

function mapStateToProps (state) {
  return {
    sidebar: state.help.sidebar,
    faq: state.faq.faq,
    error: state.faq.faqError,
    loading: state.faq.faqLoading,
    chatHasEnded: state.page.chatHasEnded,
    chatLoading: state.page.chatLoading,
    chatOnline: state.page.chatOnline
  }
}

function mapDispatchToProps (dispatch) {
  return {
    actions: bindActionCreators(actions, dispatch)
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(HelpFAQ)
